import {
  Dropdown,
  DropdownItem,
  DropdownPosition,
  DropdownToggle,
  ToolbarItem,
} from '@patternfly/react-core'
import { SortIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useState } from 'react'

import { ISortDirection } from '@app/lib/sorting'
import { invertLookupTable } from '@app/utils/lookup-table'

export enum VisitField {
  Location = 'location',
  MRN = 'mrn',
  PatientName = 'patient_name',
  StartDate = 'start_date',
  Status = 'status',
  Type = 'type',
}

export const SortingFieldLabels = {
  [VisitField.Location]: 'Location',
  [VisitField.MRN]: 'MRN',
  [VisitField.PatientName]: 'Patient Name',
  [VisitField.StartDate]: 'Start Time',
  [VisitField.Status]: 'Status',
  [VisitField.Type]: 'Type',
}

export const SortingFieldsByLabel = invertLookupTable(SortingFieldLabels)

export const SortingDirectionLabels = {
  [ISortDirection.ASC]: 'Ascending',
  [ISortDirection.DESC]: 'Descending',
}

export const SortingDirectionByLabel = invertLookupTable(SortingDirectionLabels)

interface Props {
  onSetSort: (field: string, direction: ISortDirection) => void
  sortField?: string
  sortDirection?: ISortDirection
}

export const VisitListSorting = ({
  onSetSort,
  sortDirection,
  sortField,
}: Props) => {
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState<boolean>(
    false,
  )
  const [currentField, setCurrentField] = useState<string>(
    sortField
      ? SortingFieldLabels[sortField]
      : SortingFieldLabels[VisitField.StartDate],
  )

  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState<boolean>(false)
  const [currentOrder, setCurrentOrder] = useState<string>(
    sortDirection
      ? SortingDirectionLabels[sortDirection]
      : SortingDirectionLabels[ISortDirection.DESC],
  )

  const onPropertySelect = useCallback(
    (event) => {
      const label = event.target.innerText
      setIsPropertyDropdownOpen(false)
      setCurrentField(label)
      onSetSort(
        SortingFieldsByLabel[label],
        SortingDirectionByLabel[currentOrder],
      )
    },
    [currentOrder, onSetSort],
  )

  const onPropertyToggle = useCallback((isOpen) => {
    setIsPropertyDropdownOpen(isOpen)
  }, [])

  const onOrderSelect = useCallback(
    (event) => {
      const label = event.target.innerText
      setIsOrderDropdownOpen(false)
      setCurrentOrder(label)
      onSetSort(
        SortingFieldsByLabel[currentField],
        SortingDirectionByLabel[label],
      )
    },
    [currentField, onSetSort],
  )

  const onOrderToggle = useCallback((isOpen) => {
    setIsOrderDropdownOpen(isOpen)
  }, [])

  const propertyDropDownItems = Object.keys(SortingFieldLabels).map((field) => {
    return <DropdownItem key={field}>{SortingFieldLabels[field]}</DropdownItem>
  })

  const orderDropDownItems = [
    <DropdownItem key="asc">Ascending</DropdownItem>,
    <DropdownItem key="desc">Descending</DropdownItem>,
  ]

  return (
    <React.Fragment>
      <ToolbarItem>
        <Dropdown
          onSelect={onPropertySelect}
          position={DropdownPosition.left}
          toggle={
            <DropdownToggle onToggle={onPropertyToggle}>
              <SortIcon /> {currentField}
            </DropdownToggle>
          }
          isOpen={isPropertyDropdownOpen}
          dropdownItems={propertyDropDownItems}
        />
      </ToolbarItem>
      <ToolbarItem>
        <Dropdown
          onSelect={onOrderSelect}
          position={DropdownPosition.left}
          toggle={
            <DropdownToggle onToggle={onOrderToggle}>
              {currentOrder}
            </DropdownToggle>
          }
          isOpen={isOrderDropdownOpen}
          dropdownItems={orderDropDownItems}
        />
      </ToolbarItem>
    </React.Fragment>
  )
}
